/**
 * Function to get damage checklist filter options from Redux state if available, otherwise use defaults
 */
import { FilterItem } from "../../components/LHFilter";
import { getPeriodOptions, getWeekOptions, getYearOptions } from "../../utils/filterOptions";
import { formatUserName } from "../../utils/emailTouserName";

/**
 * Helper function to transform employee filter options to show names instead of emails
 */
const transformEmployeeOptions = (options: { id: string; displayText: string }[]) => {
  return options.map(option => ({
    ...option,
    displayText: formatUserName(option.displayText) || option.displayText,
    email: option.displayText, // Store original email for API calls
  }));
};

/**
 * Helper function to find selectedOptionId for a filter
 */
const findSelectedOptionId = (
  filterKey: string,
  currentFilters: any,
  options: { id: string; displayText: string; email?: string }[]
): string | undefined => {
  const currentValue = currentFilters[filterKey];
  if (!currentValue || currentValue === "" || !options.length) return undefined;

  // For status, map the API values back to display text
  if (filterKey === "status") {
    if (currentValue === "0") return options.find(opt => opt.displayText === "Active")?.id;
    if (currentValue === "1") return options.find(opt => opt.displayText === "Complete")?.id;
  }

  // For period and week, match by ID (API uses ID)
  if (filterKey === "period" || filterKey === "week") {
    return options.find(opt => opt.id === currentValue)?.id;
  }

  // For employee filters (createdBy), try to match by email first, then displayText
  if (filterKey === "createdBy") {
    const matchedByEmail = options.find(opt => opt.email === currentValue);
    if (matchedByEmail) return matchedByEmail.id;
    
    // Fallback to displayText matching (for backward compatibility)
    return options.find(opt => opt.displayText === currentValue)?.id;
  }

  // For other filters, match by displayText
  return options.find(opt => opt.displayText === currentValue)?.id;
};

/**
 * Generates filter options for Damage Checklist tab
 * @param filtersFromRedux Object containing filter options from Redux (keys: aisle, bay, level, period, week, typeOfDamage, status, createdBy, severity)
 * @param employees Employee data from Redux state (for backward compatibility)
 * @param currentFilters Current filter values from Redux state
 * @returns Array of filter options with Redux data if available, otherwise defaults
 */
export const getDamageChecklistFilterOptions = (
  filtersFromRedux?: {
    aisle?: { id: string; displayText: string }[];
    bay?: { id: string; displayText: string }[];
    level?: { id: string; displayText: string }[];
    period?: { id: string; displayText: string }[];
    week?: { id: string; displayText: string }[];
    typeOfDamage?: { id: string; displayText: string }[];
    status?: { id: string; displayText: string }[];
    createdBy?: { id: string; displayText: string }[];
    severity?: { id: string; displayText: string }[];
  },
  employees?: any,
  currentFilters: any = {}
): FilterItem[] => {
  // Default options for each filter (using same utilities as WalkObservationsTab)
  const defaultOptions = {
    status: [
      { id: "1", displayText: "Active" },
      { id: "2", displayText: "Complete" },
    ],

    severity: [
      { id: "1", displayText: "Low" },
      { id: "2", displayText: "Medium" },
      { id: "3", displayText: "High" },
    ],
  };

  // For backward compatibility: if employees provided, use them for createdBy
  let createdByOptions = [];
  if (employees) {
    const employeeOptions = (employees?.data || []).map((emp: any) => {
      const displayName = `${emp.First_Name || ""} ${
        emp.Last_Name || ""
      }`.trim();
      return {
        id: emp.GPID || emp.Id || "",
        displayText: displayName || emp.GPID || "Unknown",
        email: emp.Email || "", // Store email for API calls
      };
    });
    if (employeeOptions.length > 0) {
      createdByOptions = employeeOptions;
    }
  } else if (
    filtersFromRedux?.createdBy &&
    filtersFromRedux.createdBy.length > 0
  ) {
    createdByOptions = transformEmployeeOptions(filtersFromRedux.createdBy);
  }

  // Generate filter items with selectedOptionId (using same utilities as WalkObservationsTab)
  const aisleOptions = filtersFromRedux?.aisle && filtersFromRedux.aisle.length > 0 ? filtersFromRedux.aisle : [];
  const bayOptions = filtersFromRedux?.bay && filtersFromRedux.bay.length > 0 ? filtersFromRedux.bay : [];
  const levelOptions = filtersFromRedux?.level && filtersFromRedux.level.length > 0 ? filtersFromRedux.level : [];
  const periodOptions = getPeriodOptions();
  const weekOptions = getWeekOptions();
  const yearOptions = getYearOptions();
  const typeOfDamageOptions = filtersFromRedux?.typeOfDamage && filtersFromRedux.typeOfDamage.length > 0 ? filtersFromRedux.typeOfDamage : [];
  const statusOptions = filtersFromRedux?.status && filtersFromRedux.status.length > 0 ? filtersFromRedux.status : defaultOptions.status;
  const severityOptions = filtersFromRedux?.severity && filtersFromRedux.severity.length > 0 ? filtersFromRedux.severity : defaultOptions.severity;

  return [
    {
      key: "aisle",
      label: "Aisle",
      options: aisleOptions,
      selectedOptionId: findSelectedOptionId("aisle", currentFilters, aisleOptions),
    },
    {
      key: "bay",
      label: "Bay",
      options: bayOptions,
      selectedOptionId: findSelectedOptionId("bay", currentFilters, bayOptions),
    },
    {
      key: "level",
      label: "Level",
      options: levelOptions,
      selectedOptionId: findSelectedOptionId("level", currentFilters, levelOptions),
    },
    {
      key: "period",
      label: "Period",
      options: periodOptions,
      selectedOptionId: findSelectedOptionId("period", currentFilters, periodOptions),
    },
    {
      key: "week",
      label: "Week",
      options: weekOptions,
      selectedOptionId: findSelectedOptionId("week", currentFilters, weekOptions),
    },
    {
      key: "year",
      label: "Year",
      options: yearOptions,
      selectedOptionId: findSelectedOptionId("year", currentFilters, yearOptions),
    },
    {
      key: "type/of/damage",
      label: "Type of Damage",
      options: typeOfDamageOptions,
      selectedOptionId: findSelectedOptionId("typeOfDamage", currentFilters, typeOfDamageOptions),
    },
    {
      key: "status",
      label: "Status",
      options: statusOptions,
      selectedOptionId: findSelectedOptionId("status", currentFilters, statusOptions),
    },
    {
      key: "created/by",
      label: "Created By",
      options: createdByOptions,
      selectedOptionId: findSelectedOptionId("createdBy", currentFilters, createdByOptions),
    },
    {
      key: "severity",
      label: "Severity",
      options: severityOptions,
      selectedOptionId: findSelectedOptionId("severity", currentFilters, severityOptions),
    },
  ];
};
